import { ReadonlyVec2, vec2, vec3 } from 'gl-matrix';
import { POLYGONS, WINDING_ODD, tesselate } from 'tess2-ts';
import { gl } from '../../../Engine';
import { SignedDistanceField } from '../../../core/models/SignedDistanceField';
import { Renderer } from '../../../core/render/Renderer';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { QuadRenderer } from '../../../core/render/renderers/QuadRenderer';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { VertexArrayObject } from '../../../core/utils/VertexArrayObject';
import { UniformBoolean } from '../../../core/utils/uniforms/UniformBoolean';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';
import {
    UniformVec2,
    UniformVec2Type,
} from '../../../core/utils/uniforms/UniformVec2';
import { UniformVec2Array } from '../../../core/utils/uniforms/UniformVec2Array';
import { UniformVec3 } from '../../../core/utils/uniforms/UniformVec3';
import { Grid } from '../../../data/common/Grid';
import { marchingSquares } from '../../../core/utils/marchingSquares';
import { ContourSequence } from '../../../data/structure/ContourSequence';
import { ContourSlice } from '../../../data/structure/ContourSlice';
import { ContourTexture } from '../../../data/structure/ContourTexture';
import { normalizeWindingOrder } from './polygonWinding';
import { maybeYield, resetGlDefaults } from './utils';

export type Buffers = {
    stencil?: VertexArrayObject;
    innerEdges?: VertexArrayObject;
    outerEdges?: VertexArrayObject;
    corners: VertexArrayObject;
};

/** Resources created for SDF rendering and cleaned up afterwards. */
interface SdfRenderTargets {
    xySdf: SignedDistanceField;
    sliceFBO: FrameBufferObject;
    destFBO: FrameBufferObject;
    quadRenderer: QuadRenderer;
    planarSize: vec2;
}

class Edge {
    private _start: ReadonlyVec2;
    private _end: ReadonlyVec2;

    public constructor(start: ReadonlyVec2, end: ReadonlyVec2) {
        this._start = start;
        this._end = end;
    }

    public get start(): ReadonlyVec2 {
        return this._start;
    }

    public get end(): ReadonlyVec2 {
        return this._end;
    }

    public get direction(): ReadonlyVec2 {
        return vec2.normalize(
            vec2.create(),
            vec2.subtract(vec2.create(), this._end, this._start)
        );
    }
}

/**
 * Generates Signed Distance Field (SDF) textures from contour data
 * using a mesh-based GPU rendering approach.
 *
 * ## Algorithm overview
 *
 * The SDF encodes, for each texel, a normalized distance to the
 * nearest contour edge. Values above 0.5 are inside the contour,
 * below 0.5 are outside, and exactly 0.5 is the boundary. The
 * distance gradient is used for smooth contour rendering and
 * contouring operations.
 *
 * Generation proceeds in four passes per slice, then a final
 * cross-slice pass:
 *
 * 1. **Stencil pass** — The contour polygons are tessellated and
 *    rendered to the stencil buffer using the odd-even fill rule.
 *    This classifies each texel as inside (stencil = 1) or
 *    outside (stencil = 0).
 *
 * 2. **Edge distance pass** — For each contour edge, a
 *    parallelogram-shaped mesh strip is built that extends
 *    perpendicular to the edge. The vertex Z coordinate encodes
 *    the normalized distance from the edge (0.5 at the edge,
 *    1.0 at the inner limit, 0.0 at the outer limit). The GPU
 *    depth test (GL_LESS for outer, GL_GREATER for inner) keeps
 *    only the closest edge's contribution per texel.
 *    Inner strips are rendered with stencil == 1 and outer
 *    strips with stencil == 0, so each texel is only affected
 *    by the correct side.
 *
 * 3. **Corner distance pass** — At each contour vertex, a quad
 *    is rendered and the fragment shader computes the circular
 *    distance from that vertex. This fills the gaps between
 *    adjacent edge strips at convex corners (where the angle
 *    between edges exceeds 90 degrees and the parallelogram
 *    strips do not overlap).
 *
 * 4. **Copy** — The finished slice is copied to the 3D SDF
 *    texture.
 *
 * 5. **Z-axis convolution** — After all slices are rendered, a
 *    final pass convolves the per-slice 2D SDFs along the
 *    normal (Z) axis, producing the full 3D distance field.
 *
 * ## Winding convention
 *
 * The edge strip construction assumes CW-wound outer contours
 * and CCW-wound holes. {@link normalizeWindingOrder} enforces
 * this at the entry point of {@link generateVAOsFromPolygons}.
 *
 * This class implements {@link Renderer} and should be accessed
 * via {@link RenderStorage.getRenderer} to ensure shader programs
 * are cached and reused across multiple SDF generations.
 *
 */
export class SdfGenerator implements Renderer {
    /**
     * {@link StencilShader} that is used to render the inside of the {@link ContourTexture} as white and stencil buffer as 1.
     */
    private stencilShader: StencilShader;

    /**
     * {@link EdgeDistanceShader} that is used to render two parallelograms for each {@link Edge}.\
     * It renders parallelograms on both sides of the edge. One inside and the other outside.\
     * Those need to be rendered separately as inside edges only apply to pixels with stencil value 1
     * and outside edges only to pixels with stencil value 0.
     */
    private edgeDistanceShader: EdgeDistanceShader;

    /**
     * {@link CornerDistanceShader} which renders two circles at each vertex point.\
     * One circle for inner distances and another for outer distances.\
     * Corners formed between two line segments that are convex (the angle between two edges > 90 degrees) are not filled by the {@link EdgeDistanceShader}.
     */
    private cornerDistanceShader: CornerDistanceShader;

    /**
     * {@link ConvolutionShader} that will render the distance information along Z-axis.\
     * After planar SDFs have been generated, we render the optimal distance amongst all the slices.
     */
    private convolutionShader: ConvolutionShader;

    public constructor() {
        this.stencilShader = new StencilShader();
        this.edgeDistanceShader = new EdgeDistanceShader();
        this.cornerDistanceShader = new CornerDistanceShader();
        this.convolutionShader = new ConvolutionShader();
    }

    // ── Render target helpers ─────────────────────────────────────────

    /**
     * Create the SDF, slice FBO, and destination FBO used by both
     * {@link renderSdf} and {@link reinitializeSdfFromSlices}.
     */
    private createSdfRenderTargets(texture: ContourTexture): SdfRenderTargets {
        const xySdf = new SignedDistanceField(texture.sizePx);
        xySdf.clearTexture();
        const planarSize = vec2.fromValues(xySdf.size[0], xySdf.size[1]);
        const sliceFBO = new FrameBufferObject(planarSize);
        sliceFBO.createTextureAttachment(
            gl.COLOR_ATTACHMENT0,
            gl.R8,
            gl.RED,
            gl.UNSIGNED_BYTE
        );
        sliceFBO.createBufferAttachment(
            gl.DEPTH_STENCIL_ATTACHMENT,
            gl.DEPTH24_STENCIL8
        );
        sliceFBO.checkStatus();
        const destFBO = new FrameBufferObject(planarSize);
        destFBO.unbind();
        const quadRenderer = RenderStorage.getRenderer(QuadRenderer);
        return { xySdf, sliceFBO, destFBO, quadRenderer, planarSize };
    }

    /** Delete all resources in a {@link SdfRenderTargets}. */
    private cleanupSdfRenderTargets(targets: SdfRenderTargets): void {
        targets.xySdf.delete();
        targets.sliceFBO.delete();
        targets.destFBO.delete();
    }

    /**
     * Convert a distance in mm to normalised texture-space
     * coordinates. Returns `undefined` when distance is zero
     * or negative.
     */
    public computeNormalizedDistances(
        distanceMm: number,
        halfSizeXy: ReadonlyVec2
    ): ReadonlyVec2 | undefined {
        if (distanceMm <= 0) {
            return undefined;
        }
        return vec2.divide(
            vec2.create(),
            vec2.fromValues(distanceMm, distanceMm),
            halfSizeXy
        );
    }

    // ── Per-slice rendering pipeline ──────────────────────────────────

    /**
     * Render a single slice through the SDF pipeline:
     * stencil -> edge distance -> corner distance -> copy to
     * destination.
     */
    private renderSlice(
        vaos: Buffers,
        innerDistances: ReadonlyVec2 | undefined,
        outerDistances: ReadonlyVec2 | undefined,
        targets: SdfRenderTargets,
        sliceIndex: number
    ): void {
        targets.sliceFBO.bind();
        if (vaos.stencil) {
            this.renderStencil(vaos.stencil);
        }
        this.renderEdgeDistance(vaos.innerEdges, vaos.outerEdges);
        this.renderCorners(vaos.corners, innerDistances, outerDistances);
        targets.destFBO.bind();
        targets.destFBO.createTextureLayer(targets.xySdf.texture, sliceIndex);
        gl.clear(gl.COLOR_BUFFER_BIT);
        targets.sliceFBO.bindRenderTarget(gl.COLOR_ATTACHMENT0);
        targets.quadRenderer.render();
        targets.destFBO.unbind();
        targets.sliceFBO.unbind();
    }

    /**
     * Renders a {@link ContourSequence} onto the {@link ContourTexture} as a Signed Distance Field.
     *
     * The texture should already be configured with correct dimensions to cover the contour sequence.
     *
     * @param texture The {@link ContourTexture} to populate with SDF data.
     * @param sequence The {@link ContourSequence} source data.
     * @param _slices Array of slices that needs updating (currently unused).
     * @param innerDistanceMm The inner margin distance in mm at which SDF value becomes 1.
     * @param outerDistanceMm The outer margin distance in mm at which SDF value becomes 0.
     * @returns Error message string if any slices were outside texture bounds, empty string otherwise.
     */
    public renderSdf(
        texture: ContourTexture,
        sequence: ContourSequence,
        _slices: number[] | undefined,
        innerDistance: number,
        outerDistance: number
    ): string {
        // renderSdf is synchronous and can be invoked mid-render
        // via the lazy RegionOfInterest.texture getter. Save the
        // caller's FBO and viewport so we can restore them
        // afterwards. These are cheap CPU-side state queries.
        const callerFbo = gl.getParameter(
            gl.FRAMEBUFFER_BINDING
        ) as WebGLFramebuffer | null;
        const callerViewport = gl.getParameter(gl.VIEWPORT) as Int32Array;

        const textureGrid = texture.textureGrid;
        const halfSizeMm = vec2.fromValues(
            textureGrid.sizeXMM / 2,
            textureGrid.sizeYMM / 2
        );
        let errorMessage = '';
        const targets = this.createSdfRenderTargets(texture);
        const innerDistances = this.computeNormalizedDistances(
            innerDistance,
            halfSizeMm
        );
        const outerDistances = this.computeNormalizedDistances(
            outerDistance,
            halfSizeMm
        );

        gl.clearColor(0, 0, 0, 0);
        gl.viewport(0, 0, targets.planarSize[0], targets.planarSize[1]);
        gl.depthMask(true);

        const textureBottom = textureGrid.getSliceCenterNormalPosition(0);
        for (const contourSlice of sequence.contourSlices) {
            const sliceIndex = Math.round(
                (contourSlice.normalPosition - textureBottom) / textureGrid.resZ
            );
            if (sliceIndex < 0 || sliceIndex >= textureGrid.sizeZ) {
                console.error(
                    `ContourSlice ${contourSlice.normalPosition} is outside of the texture bounds (${textureBottom} - ${textureBottom + textureGrid.sizeZ * textureGrid.resZ})`
                );
                errorMessage += `ContourSlice at ${contourSlice.normalPosition.toFixed(2)} is outside of the texture bounds (${textureBottom} - ${textureBottom + textureGrid.sizeZ * textureGrid.resZ})\n`;
                continue;
            }
            const vaos = this.generateVaos(
                contourSlice,
                innerDistances,
                outerDistances,
                textureGrid
            );

            this.renderSlice(
                vaos,
                innerDistances,
                outerDistances,
                targets,
                sliceIndex
            );
        }

        this.renderZDistance(
            targets.destFBO,
            texture,
            targets.xySdf,
            innerDistance,
            outerDistance
        );

        this.cleanupSdfRenderTargets(targets);
        resetGlDefaults();

        // Restore caller's FBO and viewport
        gl.bindFramebuffer(gl.FRAMEBUFFER, callerFbo);
        gl.viewport(
            callerViewport[0],
            callerViewport[1],
            callerViewport[2],
            callerViewport[3]
        );

        return errorMessage;
    }

    /**
     * Execute the stencil/edge/corner SDF rendering pipeline
     * for a single slice using caller-provided GPU resources.
     *
     * Unlike {@link renderSingleSlice}, this method does not
     * create or destroy FBOs, save/restore GL state, or compute
     * normalized distances. The caller owns the resource
     * lifecycle, making this suitable for repeated per-frame
     * calls during interactive tools.
     */
    public renderSlicePipeline(
        vaos: Buffers,
        innerDistances: ReadonlyVec2 | undefined,
        outerDistances: ReadonlyVec2 | undefined,
        sliceFBO: FrameBufferObject,
        destFBO: FrameBufferObject,
        targetTexture: WebGLTexture,
        sliceIndex: number,
        planarSize: ReadonlyVec2
    ): void {
        gl.clearColor(0, 0, 0, 0);
        gl.viewport(0, 0, planarSize[0], planarSize[1]);
        gl.depthMask(true);

        sliceFBO.bind();
        if (vaos.stencil) {
            this.renderStencil(vaos.stencil);
        }
        this.renderEdgeDistance(vaos.innerEdges, vaos.outerEdges);
        this.renderCorners(vaos.corners, innerDistances, outerDistances);

        destFBO.bind();
        destFBO.createTextureLayer(targetTexture, sliceIndex);
        gl.clear(gl.COLOR_BUFFER_BIT);
        sliceFBO.bindRenderTarget(gl.COLOR_ATTACHMENT0);
        RenderStorage.getRenderer(QuadRenderer).render();
        destFBO.unbind();
        sliceFBO.unbind();

        resetGlDefaults();
    }

    /**
     * Render the SDF for a single slice into {@link sliceFBO}
     * without copying to a 3D texture layer. This is the first
     * half of {@link renderSlicePipeline}, useful when the
     * caller needs the rendered data in a non-Z orientation.
     */
    public renderSliceToFbo(
        vaos: Buffers,
        innerDistances: ReadonlyVec2 | undefined,
        outerDistances: ReadonlyVec2 | undefined,
        sliceFBO: FrameBufferObject,
        planarSize: ReadonlyVec2
    ): void {
        gl.clearColor(0, 0, 0, 0);
        gl.viewport(0, 0, planarSize[0], planarSize[1]);
        gl.depthMask(true);

        sliceFBO.bind();
        if (vaos.stencil) {
            this.renderStencil(vaos.stencil);
        }
        this.renderEdgeDistance(vaos.innerEdges, vaos.outerEdges);
        this.renderCorners(vaos.corners, innerDistances, outerDistances);
        sliceFBO.unbind();

        resetGlDefaults();
    }

    /**
     * Reinitialize an existing Uint8 SDF by reading pixel data from the GPU,
     * extracting iso-contours via marching squares, and rendering them through
     * the same GPU-based SDF pipeline used by {@link renderSdf}.
     *
     * @param texture The {@link ContourTexture} whose SDF will be reinitialized.
     * @param innerDistance The distance inside the ROI (in mm) at which pixels become 1.
     * @param outerDistance The distance outside the ROI (in mm) at which pixels become 0.
     * @returns Array of {@link Uint8Array} slices containing the reinitialized SDF data.
     */
    public async renderSDFFromTexture(
        texture: ContourTexture,
        innerDistance: number,
        outerDistance: number
    ): Promise<Uint8Array[]> {
        const sliceData = texture.signedDistanceField.toArray();
        return this.reinitializeSdfFromSlices(
            texture,
            innerDistance,
            outerDistance,
            sliceData
        );
    }

    /**
     * Reinitialize an SDF using caller-provided CPU pixel data, skipping
     * GPU readback entirely. Use this when the pixel data is already
     * available in CPU memory.
     *
     * @param texture The {@link ContourTexture} used as the GPU write target.
     * @param innerDistance The distance inside the ROI (in mm) at which pixels become 1.
     * @param outerDistance The distance outside the ROI (in mm) at which pixels become 0.
     * @param pixelData Pre-read CPU-side pixel data (one Uint8Array per slice).
     * @returns Array of {@link Uint8Array} slices containing the reinitialized SDF data.
     */
    public async renderSDFFromPixelData(
        texture: ContourTexture,
        innerDistance: number,
        outerDistance: number,
        pixelData: Uint8Array[]
    ): Promise<Uint8Array[]> {
        return this.reinitializeSdfFromSlices(
            texture,
            innerDistance,
            outerDistance,
            pixelData
        );
    }

    /**
     * Shared core for SDF reinitialization from CPU pixel data.
     *
     * The work is split into CPU and GPU phases so that yields
     * only happen during CPU-intensive work (marching squares,
     * VAO building) and the GPU rendering runs without
     * interruption.
     *
     * Phase 1 (CPU): marching squares contour extraction
     * Phase 2 (CPU): VAO mesh building from the extracted
     *                contours
     * Phase 3 (GPU): stencil + edge + corner rendering for
     *                every slice, then Z-axis convolution
     * Phase 4 (CPU): result readback
     */
    private async reinitializeSdfFromSlices(
        texture: ContourTexture,
        innerDistance: number,
        outerDistance: number,
        sliceData: Uint8Array[]
    ): Promise<Uint8Array[]> {
        const textureGrid = texture.textureGrid;
        const halfSizeMm = vec2.fromValues(
            textureGrid.sizeXMM / 2,
            textureGrid.sizeYMM / 2
        );

        const sizeX = textureGrid.sizeX;
        const sizeY = textureGrid.sizeY;
        const sizeZ = textureGrid.sizeZ;

        const innerDistances = this.computeNormalizedDistances(
            innerDistance,
            halfSizeMm
        );
        const outerDistances = this.computeNormalizedDistances(
            outerDistance,
            halfSizeMm
        );

        // Phase 1 (CPU): Extract iso-contours via marching squares
        // and transform to clip space [-1, 1].
        const threshold = ContourTexture.SDF_THRESHOLD;

        let lastYieldTick = performance.now();
        const slicePolygons: (vec2[][] | null)[] = new Array(sizeZ);
        for (let z = 0; z < sizeZ; z++) {
            const polygons = marchingSquares(
                sliceData[z],
                sizeX,
                sizeY,
                1,
                1,
                threshold
            );
            if (polygons.length === 0) {
                slicePolygons[z] = null;
                continue;
            }
            for (const poly of polygons) {
                for (const v of poly) {
                    const clip = textureGrid.voxelToClipSpacePosition(
                        vec3.fromValues(v[0], v[1], z + 0.5)
                    );
                    v[0] = clip[0];
                    v[1] = clip[1];
                }
            }
            slicePolygons[z] = polygons;
            lastYieldTick = await maybeYield(lastYieldTick);
        }

        // Phase 2 (CPU): Build VAO mesh data for each slice.
        // This includes winding normalization, edge
        // parallelogram construction, corner quads, and
        // tessellation — all CPU-bound.
        const sliceVaos: (Buffers | null)[] = new Array(sizeZ);
        for (let z = 0; z < sizeZ; z++) {
            const polygons = slicePolygons[z];
            if (!polygons) {
                sliceVaos[z] = null;
                continue;
            }
            sliceVaos[z] = this.generateVAOsFromPolygons(
                polygons,
                innerDistances,
                outerDistances
            );
            lastYieldTick = await maybeYield(lastYieldTick);
        }

        // Phase 3 (GPU): Render all slices without yielding.
        // GL state is set up once and not interrupted.
        const targets = this.createSdfRenderTargets(texture);

        gl.clearColor(0, 0, 0, 0);
        gl.viewport(0, 0, targets.planarSize[0], targets.planarSize[1]);
        gl.depthMask(true);

        for (let z = 0; z < sizeZ; z++) {
            const vaos = sliceVaos[z];
            if (!vaos) {
                continue;
            }
            this.renderSlice(vaos, innerDistances, outerDistances, targets, z);
        }

        this.renderZDistance(
            targets.destFBO,
            texture,
            targets.xySdf,
            innerDistance,
            outerDistance
        );

        // Phase 4: Read back results
        const sdf = texture.signedDistanceField;
        const result = sdf.toArray();

        this.cleanupSdfRenderTargets(targets);
        resetGlDefaults();

        return result;
    }

    /**
     * Method to render the stencil buffer.\
     * Pixels within the contour are given stencil value of 1.\
     * Pixels outside the contour are given stencil value of 0.
     * @param vao {@link VertexArrayObject} that contains the triangulated mesh for which the stencil texture is rendered.
     * @param fillColor number that defines grayscale color which the covered pixels should have. Defaults to 1.
     * @returns True to indicate that the stencil map was successfully rendered.
     */
    private renderStencil(vao: VertexArrayObject, fillColor = 1): boolean {
        //render to stencil FBO's stencil attachment.
        gl.enable(gl.STENCIL_TEST);
        gl.enable(gl.DEPTH_TEST);
        gl.depthMask(true);
        gl.stencilMask(0xff);
        gl.clear(
            gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT
        );
        gl.disable(gl.DEPTH_TEST);
        this.stencilShader.start();
        this.stencilShader.fillColor.loadVec3(
            vec3.fromValues(fillColor, fillColor, fillColor)
        );

        gl.stencilFunc(gl.ALWAYS, 1, 0x00); //every fragment passes the stencil test
        gl.stencilOp(gl.INVERT, gl.INVERT, gl.INVERT); //fragment's stencil value is always inverted

        vao.bind(0);
        //render
        gl.drawElements(gl.TRIANGLES, vao.indexCount, gl.UNSIGNED_INT, 0);
        vao.unbind(0);
        vao.delete();
        gl.stencilMask(0x00);
        this.stencilShader.stop();
        return true;
    }

    /**
     * Method to render SDF edge distances on the distance texture.\
     * This renders border as 0.5 and color values inwards and outwards from the edge going linearly towards 1 or 0 respectively.
     * @param innerEdges {@link VertexArrayObject} that presents the mesh data inwards from each edge.
     * @param outerEdges {@link VertexArrayObject} that presents the mesh data outwards from each edge.
     * @returns True to indicate that edges were successfully rendered.
     */
    private renderEdgeDistance(
        innerEdges: VertexArrayObject | undefined,
        outerEdges: VertexArrayObject | undefined
    ): boolean {
        //render to distanceFBO's color attachment 0.
        gl.enable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);

        this.edgeDistanceShader.start();
        if (innerEdges) {
            gl.blendEquation(gl.MIN);
            gl.stencilFunc(gl.EQUAL, 0xff, 0xff);
            innerEdges.bind(0);
            gl.drawElements(
                gl.TRIANGLES,
                innerEdges.indexCount,
                gl.UNSIGNED_INT,
                0
            );
            innerEdges.unbind(0);
            innerEdges.delete();
        }
        if (outerEdges) {
            gl.blendEquation(gl.MAX);
            gl.stencilFunc(gl.EQUAL, 0x00, 0xff);
            outerEdges.bind(0);
            gl.drawElements(
                gl.TRIANGLES,
                outerEdges.indexCount,
                gl.UNSIGNED_INT,
                0
            );
            outerEdges.unbind(0);
            outerEdges.delete();
        }
        this.edgeDistanceShader.stop();
        return true;
    }

    /**
     * Method to render SDF corner distances.\
     * This renders distance relative to each of the contour's corner points
     * @param vao {@link VertexArrayObject} that stores coordinates of a square around each vertex point.
     * @param innerDistance The maximum inner distance in mm, at which point color turns to 1.
     * @param outerDistance The maximum outer distance in mm, at which point color turns to 0.
     * @returns True to indicate that the corners were successfully rendered.
     */
    private renderCorners(
        vao: VertexArrayObject,
        innerDistances: ReadonlyVec2 | undefined,
        outerDistances: ReadonlyVec2 | undefined
    ): boolean {
        this.cornerDistanceShader.start();
        vao.bind(0, 1);
        if (innerDistances) {
            gl.blendEquation(gl.MIN);
            this.cornerDistanceShader.scale.loadVec2(innerDistances);
            gl.stencilFunc(gl.EQUAL, 0xff, 0xff);
            this.cornerDistanceShader.invert.loadBoolean(false);
            gl.drawElements(gl.TRIANGLES, vao.indexCount, gl.UNSIGNED_INT, 0);
        }
        if (outerDistances) {
            gl.blendEquation(gl.MAX);
            this.cornerDistanceShader.scale.loadVec2(outerDistances);
            gl.stencilFunc(gl.EQUAL, 0x00, 0xff);
            this.cornerDistanceShader.invert.loadBoolean(true);
            gl.drawElements(gl.TRIANGLES, vao.indexCount, gl.UNSIGNED_INT, 0);
        }
        vao.unbind(0, 1);
        vao.delete();
        this.cornerDistanceShader.stop();

        gl.disable(gl.DEPTH_TEST);
        gl.disable(gl.STENCIL_TEST);
        return true;
    }

    /**
     * Method to render the optimal distance along the normal direction for each vertex.\
     * This converts planar SDF into a 3D SDF.\
     * Also as this is the final rendering step, it performs the rendering operation on the {@link ContourTexture}'s {@link SignedDistanceField}.
     * @param destFBO {@link FrameBufferObject} on which the {@link destination} {@link ContourTexture}'s {@link ContourTexture.sdf SDF} is bound for rendering.
     * @param texture {@link ContourTexture} on which the {@link source} {@link SignedDistanceField}'s data is rendered.
     * @param innerDistance The maximum inner distance in mm, at which point color turns to 1.
     * @param outerDistance The maximum outer distance in mm, at which point color turns to 0.
     * @returns True to indicate that the distance along z-axis was successfully rendered.
     */
    private renderZDistance(
        destFBO: FrameBufferObject,
        destination: ContourTexture,
        source: SignedDistanceField,
        innerDistance: number,
        outerDistance: number
    ): boolean {
        const sdf = destination.signedDistanceField;
        if (!sdf) {
            throw new Error(
                `Attempt to render ZDistance for ContourTexture without SDF`
            );
        }

        destFBO.bind();
        gl.disable(gl.BLEND);
        gl.bindTexture(gl.TEXTURE_3D, source.texture);
        this.convolutionShader.start();
        RenderStorage.getQuadVAO().bind(0);

        const destGrid = destination.textureGrid;
        const sizeZ = destGrid.sizeZ;
        const maxDistance = Math.max(innerDistance, outerDistance);

        // Slice spacing is uniform (single resZ scalar), so
        // signed Z-distance between slices is just delta * resZ.
        // Precompute the kernel radius once instead of walking
        // outward with getSliceCenterNormalPosition per neighbor.
        const sliceSpacingMm = destGrid.resZ;
        // +1 to include one slice beyond maxDistance for
        // boundary crossing interpolation in the shader.
        const kernelRadius = Math.ceil(maxDistance / sliceSpacingMm) + 1;

        // Precompute per-distance normalizations that are the
        // same for every slice (only texture Z changes).
        const innerScale =
            innerDistance > 0 ? sliceSpacingMm / innerDistance : 0;
        const outerScale =
            outerDistance > 0 ? sliceSpacingMm / outerDistance : 0;

        // Build convolution arrays once. Both components are
        // invariant across slices:
        //   X = normalized Z-distance = delta * spacing / margin
        //   Y = relative texture Z offset = delta / sizeZ
        // The shader computes the absolute texture Z as k + Y,
        // and CLAMP_TO_EDGE on the 3D texture handles volume
        // edges (out-of-bounds samples repeat the edge slice).
        const fullSize = 2 * kernelRadius + 1;
        const invSizeZ = 1 / sizeZ;
        const innerConv =
            innerDistance > 0
                ? Array.from({ length: fullSize }, (_, k) => {
                      const delta = k - kernelRadius;
                      return vec2.fromValues(
                          delta * innerScale,
                          delta * invSizeZ
                      );
                  })
                : [];
        const outerConv =
            outerDistance > 0
                ? Array.from({ length: fullSize }, (_, k) => {
                      const delta = k - kernelRadius;
                      return vec2.fromValues(
                          delta * outerScale,
                          delta * invSizeZ
                      );
                  })
                : [];

        this.convolutionShader.loadConvolutions(innerConv, outerConv);
        this.convolutionShader.convolutionSize.loadVec2(
            innerConv.length,
            outerConv.length
        );

        for (let sliceIdx = 0; sliceIdx < sizeZ; ++sliceIdx) {
            this.convolutionShader.k.loadFloat((sliceIdx + 0.5) * invSizeZ);
            destFBO.createTextureLayer(sdf.texture, sliceIdx);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }

        this.convolutionShader.stop();
        gl.bindTexture(gl.TEXTURE_3D, null);
        RenderStorage.getQuadVAO().unbind(0);
        destFBO.unbind();
        return true;
    }

    /**
     * Method to generate the mesh that is to be rendered on the texture slice.\
     * This generates multiple {@link VertexArrayObject}s. Each presenting a mesh required by different rendering stages.
     *
     * @param slice {@link ContourSlice} for which the {@link VertexArrayObject}s are being generated.
     * @param innerDistances {@link ReadonlyVec2} that represents the distance inside the mesh relative to texture size at which point color values become 1.
     * @param outerDistances {@link ReadonlyVec2} that represents the distance outside the mesh relative to texture size at which point color values become 0.
     * @param grid {@link Grid} representing the texture grid used for world-to-clip-space transformation.
     * @returns A collection of {@link VertexArrayObject}s that represent different meshes required in the SDF generation process.
     */
    private generateVaos(
        slice: ContourSlice,
        innerDistances: ReadonlyVec2 | undefined,
        outerDistances: ReadonlyVec2 | undefined,
        grid: Grid
    ): Buffers {
        const polygons: vec2[][] = [];

        for (const contour of slice.contours) {
            const polygon: vec2[] = [];
            for (const vertex of contour) {
                const clip = grid.worldToClipSpacePosition(vertex);
                // Small tolerance for floating-point round-trip
                // errors in oblique coordinate transforms.
                const EPS = 1e-3;
                if (
                    clip[0] < -1 - EPS ||
                    clip[0] > 1 + EPS ||
                    clip[1] < -1 - EPS ||
                    clip[1] > 1 + EPS
                ) {
                    throw new Error(
                        'Critical error in SDF generation.' +
                            ` TransformedVertex {${clip[0]}, ${clip[1]}}` +
                            ' is not within texture'
                    );
                }
                const cx = Math.max(-1, Math.min(1, clip[0]));
                const cy = Math.max(-1, Math.min(1, clip[1]));
                polygon.push(vec2.fromValues(cx, cy));
            }
            polygons.push(polygon);
        }

        return this.generateVAOsFromPolygons(
            polygons,
            innerDistances,
            outerDistances
        );
    }

    /**
     * Build the GPU mesh data (VAOs) used by the SDF rendering
     * passes from polygon data in [-1, 1] NDC space.
     *
     * Produces four VAOs returned as {@link Buffers}:
     *
     * - **stencil** — Tessellated fill mesh for the stencil pass.
     * - **innerEdges** — Parallelogram strips extending inward
     *   from each edge. Rendered with stencil == 1 so they only
     *   affect texels inside the contour.
     * - **outerEdges** — Parallelogram strips extending outward.
     *   Rendered with stencil == 0 (outside texels only).
     * - **corners** — A quad per vertex for circular distance
     *   fill at convex corners.
     *
     * Winding is normalised at the top of this method via
     * {@link normalizeWindingOrder} so that outer contours are
     * CW and holes are CCW, regardless of the input ordering.
     */
    public generateVAOsFromPolygons(
        polygons: vec2[][],
        innerDistances: ReadonlyVec2 | undefined,
        outerDistances: ReadonlyVec2 | undefined
    ): Buffers {
        polygons = normalizeWindingOrder(polygons);
        const stencilArrays: number[][] = [];
        let edgeIndex = 0;
        type Builder = {
            vertices: Float32Array;
            indices: Int32Array;
        };

        /**
         * Write one parallelogram triangle-strip for an edge.
         *
         * The strip has three vertices: the edge start on the
         * contour boundary (Z = 0.5), then the edge start and
         * end offset perpendicular to the edge by `distances`.
         * The offset vertices get `zValue` as their Z, which
         * encodes the SDF limit (1.0 for inner, 0.0 for outer).
         * The GPU interpolates Z linearly across the strip,
         * producing the distance gradient.
         *
         * The perpendicular offset direction is determined by
         * `perpSign`:
         *   perpSign = +1 → right-hand side of edge direction
         *                    (inward for CW-wound polygons)
         *   perpSign = -1 → left-hand side
         *                    (outward for CW-wound polygons)
         */
        function writeEdgeParallelogram(
            builder: Builder,
            edge: Edge,
            dir: ReadonlyVec2,
            distances: ReadonlyVec2,
            perpSign: number,
            zValue: number,
            indices: number[],
            vertexOffset: number,
            indexOffset: number
        ): void {
            // Vertex on the contour edge itself (Z = 0.5 = boundary)
            builder.vertices.set(
                [edge.start[0], edge.start[1], 0.5],
                vertexOffset
            );
            // Vertices offset perpendicular to the edge
            // (Z = zValue = the SDF limit for this side)
            builder.vertices.set(
                [
                    edge.start[0] + perpSign * dir[1] * distances[0],
                    edge.start[1] - perpSign * dir[0] * distances[1],
                    zValue,
                ],
                vertexOffset + 3
            );
            builder.vertices.set(
                [
                    edge.end[0] + perpSign * dir[1] * distances[0],
                    edge.end[1] - perpSign * dir[0] * distances[1],
                    zValue,
                ],
                vertexOffset + 6
            );
            builder.indices.set(indices, indexOffset);
        }

        let numberOfEdges = 0;
        let totalVertices = 0;
        for (const poly of polygons) {
            const verts =
                poly.length > 0 && vec2.equals(poly[0], poly[poly.length - 1])
                    ? poly.length - 1
                    : poly.length;
            numberOfEdges += verts;
            totalVertices += verts;
        }

        const dir = vec2.create();
        let innerBuilder: Builder | undefined = undefined;
        let outerBuilder: Builder | undefined = undefined;
        if (innerDistances) {
            innerBuilder = {
                vertices: new Float32Array(numberOfEdges * 9),
                indices: new Int32Array(numberOfEdges * 6),
            };
        }
        if (outerDistances) {
            outerBuilder = {
                vertices: new Float32Array(numberOfEdges * 9),
                indices: new Int32Array(numberOfEdges * 6),
            };
        }

        // Perpendicular direction for inner vs outer edge strips.
        // For CW-wound outer contours, +1 points inward (right of
        // edge direction) and -1 points outward (left of edge
        // direction). The stencil test ensures each strip only
        // writes to its own side (inner to stencil=1, outer to
        // stencil=0).
        const innerPerpSign = 1;
        const outerPerpSign = -1;

        const handleEdge = (
            edge: Edge,
            edgeIndex: number,
            firstIndex: number,
            finalIndex: number
        ): void => {
            vec2.copy(dir, edge.direction);
            const vertexOffset = edgeIndex * 9;
            const indexOffset = edgeIndex * 6;
            const min = edgeIndex * 3;
            let max = min + 3;
            if (max === finalIndex) {
                max = firstIndex;
            }
            if (innerBuilder) {
                writeEdgeParallelogram(
                    innerBuilder,
                    edge,
                    dir,
                    innerDistances!,
                    innerPerpSign,
                    1,
                    [max, min + 2, min, min, min + 2, min + 1],
                    vertexOffset,
                    indexOffset
                );
            }
            if (outerBuilder) {
                writeEdgeParallelogram(
                    outerBuilder,
                    edge,
                    dir,
                    outerDistances!,
                    outerPerpSign,
                    0,
                    [min + 2, max, min + 1, min + 1, max, min],
                    vertexOffset,
                    indexOffset
                );
            }
        };

        // Corner quads: at each contour vertex we build a quad
        // (two triangles, four vertices) whose fragment shader
        // computes the circular distance to that vertex. This
        // fills gaps at convex corners that the edge strips miss.
        // cornerVertexCoordinates stores the vertex position
        // (repeated 4 times per quad); cornerDirections stores
        // the quad corner offsets (-1/+1 combinations) used by
        // the shader to expand the quad around the vertex.
        let index4 = 0;
        let index6 = 0;
        let index8 = 0;
        const cornerIndices = new Int32Array(totalVertices * 6);
        const cornerVertexCoordinates = new Float32Array(totalVertices * 8);
        const cornerDirections = new Float32Array(totalVertices * 8);

        let firstIndex = 0;

        for (const poly of polygons) {
            const closed =
                poly.length > 0 && vec2.equals(poly[0], poly[poly.length - 1]);
            const vertCount = closed ? poly.length - 1 : poly.length;
            const finalIndex = firstIndex + 3 * vertCount;

            const stencilVertices = new Array<number>(vertCount * 2);
            let i2 = 0;
            let previous: vec2 | undefined = undefined;
            let first: vec2 | undefined = undefined;

            for (let vi = 0; vi < vertCount; vi++) {
                const vx = poly[vi][0];
                const vy = poly[vi][1];

                stencilVertices[i2++] = vx;
                stencilVertices[i2++] = vy;

                if (previous) {
                    handleEdge(
                        new Edge(previous, vec2.fromValues(vx, vy)),
                        edgeIndex++,
                        firstIndex,
                        finalIndex
                    );
                    vec2.set(previous, vx, vy);
                } else {
                    previous = vec2.fromValues(vx, vy);
                    first = vec2.clone(previous);
                }

                cornerIndices.set(
                    [
                        index4,
                        index4 + 1,
                        index4 + 2,
                        index4 + 2,
                        index4 + 1,
                        index4 + 3,
                    ],
                    index6
                );
                index6 += 6;
                index4 += 4;
                cornerDirections.set(
                    [-1.0, 1.0, -1.0, -1.0, 1.0, 1.0, 1.0, -1.0],
                    index8
                );
                cornerVertexCoordinates.set(
                    [vx, vy, vx, vy, vx, vy, vx, vy],
                    index8
                );
                index8 += 8;
            }
            if (previous && first) {
                handleEdge(
                    new Edge(previous, first),
                    edgeIndex++,
                    firstIndex,
                    finalIndex
                );
            }
            stencilArrays.push(stencilVertices);
            firstIndex = finalIndex;
        }

        // Tessellate the polygon outlines into a triangle mesh
        // for the stencil fill pass. The odd-even winding rule
        // correctly handles holes: overlapping regions cancel
        // out, leaving only the filled area.
        const tess = tesselate({
            windingRule: WINDING_ODD,
            elementType: POLYGONS,
            polySize: 3,
            vertexSize: 2,
            contours: stencilArrays,
            strict: false,
        });
        let stencilVAO: VertexArrayObject | undefined = undefined;
        if (tess) {
            stencilVAO = new VertexArrayObject();
            stencilVAO.bind();
            stencilVAO.createAttribute(0, gl.FLOAT, tess.vertices, 2);
            stencilVAO.createIndexBuffer(tess.elements);
        }

        // Upload all geometry into GPU vertex array objects.
        let innerVAO: VertexArrayObject | undefined = undefined;
        let outerVAO: VertexArrayObject | undefined = undefined;
        if (innerBuilder) {
            innerVAO = new VertexArrayObject();
            innerVAO.bind();
            innerVAO.createIndexBuffer(innerBuilder.indices);
            innerVAO.createAttribute(0, gl.FLOAT, innerBuilder.vertices, 3);
            innerVAO.unbind();
        }
        if (outerBuilder) {
            outerVAO = new VertexArrayObject();
            outerVAO.bind();
            outerVAO.createIndexBuffer(outerBuilder.indices);
            outerVAO.createAttribute(0, gl.FLOAT, outerBuilder.vertices, 3);
            outerVAO.unbind();
        }

        const cornerVAO = new VertexArrayObject();
        cornerVAO.bind();
        cornerVAO.createIndexBuffer(cornerIndices);
        cornerVAO.createAttribute(0, gl.FLOAT, cornerVertexCoordinates, 2);
        cornerVAO.createAttribute(1, gl.FLOAT, cornerDirections, 2);
        cornerVAO.unbind();

        return {
            stencil: stencilVAO,
            innerEdges: innerVAO,
            outerEdges: outerVAO,
            corners: cornerVAO,
        };
    }

    /**
     * Method to delete the {@link ShaderProgram shaders} used in the {@link SDFMesh.renderSDF SDFMesh generation process}.\
     * It is important to call this method when {@link SDFMesh} is no longer needed to de-allocate the WebGL memory.
     * Deletes all {@link ShaderProgram shaders} used by this generator.
     *
     * Note: When using {@link RenderStorage.getRenderer}, the lifecycle is managed by RenderStorage
     * and this method will be called automatically when the WebGL context is destroyed.
     *
     * @returns True to indicate successful deletion.
     */
    public delete() {
        this.stencilShader.delete();
        this.edgeDistanceShader.delete();
        this.cornerDistanceShader.delete();
        this.convolutionShader.delete();
    }
}

/**
 * Used for rendering the stencil map
 */
class StencilShader extends ShaderProgram {
    private static VERTEX_CONTENT = `#version 300 es
    precision highp float;

    in vec2 position;

    void main() {
        gl_Position = vec4(position, 0.0, 1.0);
    }
    `;

    private static FRAGMENT_CONTENT = `#version 300 es
    precision highp float;

    out vec4 color;

    uniform vec3 fillColor;

    void main() {
        color = vec4(fillColor.rgb, 1.0);
    }
    `;

    public readonly fillColor: UniformVec3;

    constructor() {
        super(
            StencilShader.VERTEX_CONTENT,
            StencilShader.FRAGMENT_CONTENT,
            'position'
        );
        this.fillColor = new UniformVec3('fillColor');
        super.storeUniformLocations(this.fillColor);
    }
}

class EdgeDistanceShader extends ShaderProgram {
    private static VERTEX_CONTENT = `#version 300 es
    precision highp float;
    
    in vec3 position;

    out float pass_distance;
    
    void main() {
        gl_Position = vec4(position, 1.0);
        pass_distance = position.z;
        gl_Position.z = 2.0 * abs(position.z - 0.5);
    }
    `;

    private static FRAGMENT_CONTENT = `#version 300 es
    precision highp float;

    in float pass_distance;
    
    out vec4 color;
    
    void main(){
        color = vec4(pass_distance, pass_distance, pass_distance, 1.0);
    }
    `;

    constructor() {
        super(
            EdgeDistanceShader.VERTEX_CONTENT,
            EdgeDistanceShader.FRAGMENT_CONTENT,
            'position'
        );
    }
}

class CornerDistanceShader extends ShaderProgram {
    private static VERTEX_CONTENT = `#version 300 es
    precision highp float;
    in vec2 position;
    in vec2 direction;
    //scale relative to image size
    uniform vec2 scale;
    out vec2 pass_distance;
    void main() {
        gl_Position = vec4(position + direction * scale, 1.0, 1.0);
        pass_distance = direction;
    }
    `;

    private static FRAGMENT_CONTENT = `#version 300 es
    precision highp float;
    in vec2 pass_distance;
    out vec4 color;
    uniform bool invert;
    void main() {
        float distance = length(pass_distance);
        if(distance >= 1.0){
            discard;
        }
        gl_FragDepth = distance;
        float colorValue = invert ? 0.5 - 0.5 * distance : 0.5 + 0.5 * distance;
        color = vec4(colorValue, colorValue, colorValue, 1.0);
    }
    `;

    public scale: UniformVec2;
    public invert: UniformBoolean;

    constructor() {
        super(
            CornerDistanceShader.VERTEX_CONTENT,
            CornerDistanceShader.FRAGMENT_CONTENT,
            'position',
            'direction'
        );
        this.scale = new UniformVec2('scale');
        this.invert = new UniformBoolean('invert');
        this.storeUniformLocations(this.scale, this.invert);
    }
}

class ConvolutionShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
    
    precision highp float;

    in vec2 position;

    out vec2 texCoord; //screenPosition of the pixel being processed [0, 1]

    void main() {
        gl_Position = vec4(position, 0.0, 1.0);
        texCoord = vec2(position.x + 1.0, position.y + 1.0) * 0.5; // {0 - 1, 0 - 1}
    }
    `;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
    
    precision highp float;
    precision highp sampler3D;

    in vec2 texCoord;

    out vec4 color;

    uniform sampler3D sampler;
    // Slice position in texture coordinates [0, 1]
    uniform float k;

    // vec2(normalizedDist, relativeTexZ) sorted by signed distance.
    // normalizedDist = signedDistMm / marginDistMm.
    // relativeTexZ = slice offset in texture coordinates
    //                (add to k to get absolute texture Z).
    uniform vec2 innerConvolution[100];
    uniform vec2 outerConvolution[100];

    // lengths of the inner and outer convolution arrays to iterate over
    uniform ivec2 convolutionSize;

    float calculateNormalizedDistance(float r) {
        return 2.0 * abs(r - 0.5);
    }

    vec4 sampleTexture(vec2 uv, float sliceK) {
        return texture(sampler, vec3(uv, sliceK));
    }

    void main(){
        vec4 original = sampleTexture(texCoord, k);
        bool within = original.r > 0.5;
        // Start with the XY distance on the current slice
        float minDist = calculateNormalizedDistance(original.r);

        int size = within ? convolutionSize.x : convolutionSize.y;
        float prevSdfValue = 0.0;
        for (int i = 0; i < size; ++i) {
            vec2 element = within ? innerConvolution[i] : outerConvolution[i];
            float signedRelativeDistance = element.x;
            float absoluteRelativeDistance = abs(signedRelativeDistance);
            float sliceK = k + element.y;
            float sdfValue = sampleTexture(texCoord, sliceK).r;

            // Same side as pixel: compute 3D distance
            if (within == (sdfValue > 0.5)) {
                if (absoluteRelativeDistance < minDist) {
                    float xyDist = calculateNormalizedDistance(sdfValue);
                    minDist = min(minDist, length(vec2(xyDist, absoluteRelativeDistance)));
                }
            }

            // Boundary crossing between consecutive slices
            if (i > 0 && (prevSdfValue > 0.5) != (sdfValue > 0.5)) {
                vec2 prevElement = within
                    ? innerConvolution[i - 1]
                    : outerConvolution[i - 1];
                float prevSignedRelativeDistance = prevElement.x;
                float interpolationFactor = (0.5 - prevSdfValue) / (sdfValue - prevSdfValue);
                float crossingDistance = abs(
                    mix(prevSignedRelativeDistance, signedRelativeDistance, interpolationFactor)
                );
                minDist = min(minDist, crossingDistance);
            }

            prevSdfValue = sdfValue;
        }
        float result = within ? 0.5 + minDist * 0.5 : 0.5 - minDist * 0.5;
        color = vec4(result, result, result, original.a);
    }
    `;

    public k: UniformFloat;
    public innerConvolution: UniformVec2Array;
    public outerConvolution: UniformVec2Array;
    public convolutionSize: UniformVec2;

    public constructor() {
        super(
            ConvolutionShader.VERTEX_CONTENT,
            ConvolutionShader.FRAGMENT_CONTENT,
            'position'
        );
        this.k = new UniformFloat('k');
        this.innerConvolution = new UniformVec2Array('innerConvolution');
        this.outerConvolution = new UniformVec2Array('outerConvolution');
        this.convolutionSize = new UniformVec2(
            'convolutionSize',
            UniformVec2Type.INTEGER
        );
        super.storeUniformLocations(this.k, this.convolutionSize);
    }

    public loadConvolutions(inner: ReadonlyVec2[], outer: ReadonlyVec2[]) {
        if (this.programID) {
            // Entries must be sorted by signed distance so
            // consecutive entries are spatially adjacent slices.
            // Callers are expected to provide them in order.
            this.innerConvolution.loadVectors(this.programID, inner);
            this.outerConvolution.loadVectors(this.programID, outer);
        }
    }
}
